/* ============================================
   FORTERA APPAREL - ANIMATIONS & INTERACTIONS
   ============================================ */

(function () {
  'use strict';

  /* === Scroll Reveal Animation === */
  class ForteraScrollReveal {
    constructor() {
      this.elements = document.querySelectorAll('.fortera-animate');
      if (this.elements.length === 0) return;
      this.init();
    }

    init() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      this.elements.forEach((el) => observer.observe(el));
    }
  }

  /* === Tab Collection Component === */
  class ForteraTabCollection extends HTMLElement {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('[data-fortera-tab]');
      this.panels = this.querySelectorAll('[data-fortera-tab-panel]');
      this.init();
    }

    init() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.forteraTab;
          this.activateTab(target);
        });
      });
    }

    activateTab(target) {
      this.tabs.forEach((tab) => {
        tab.classList.toggle('is-active', tab.dataset.forteraTab === target);
      });
      this.panels.forEach((panel) => {
        const isTarget = panel.dataset.forteraTabPanel === target;
        panel.classList.toggle('is-active', isTarget);

        // Recalculate slider widths in newly visible panel
        if (isTarget) {
          requestAnimationFrame(() => {
            const sliders = panel.querySelectorAll('fortera-slider');
            sliders.forEach((slider) => {
              if (typeof slider.updateSlideWidths === 'function') {
                slider.updateSlideWidths();
              }
            });
          });
        }
      });
    }
  }

  customElements.define('fortera-tab-collection', ForteraTabCollection);

  /* === Product Slider / Carousel === */
  class ForteraSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // Defer init to next frame so layout is calculated
      requestAnimationFrame(() => {
        this.track = this.querySelector('[data-fortera-slider-track]');
        this.slides = this.querySelectorAll('[data-fortera-slide]');
        this.prevBtn = this.querySelector('[data-fortera-prev]');
        this.nextBtn = this.querySelector('[data-fortera-next]');
        this.dots = this.querySelectorAll('[data-fortera-dot]');
        this.slidesPerView = parseInt(this.dataset.slidesPerView) || 1;
        this.autoplay = this.dataset.autoplay === 'true';
        this.autoplayInterval = parseInt(this.dataset.autoplaySpeed) || 5000;
        this.init();
      });
    }

    init() {
      if (!this.track || this.slides.length === 0) return;

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });

      if (this.autoplay) {
        this.startAutoplay();
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
      }

      // Touch support
      let startX = 0;
      let startY = 0;
      let isDragging = false;
      let direction = null;

      this.track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
        direction = null;
      }, { passive: true });

      this.track.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        const dx = Math.abs(e.touches[0].clientX - startX);
        const dy = Math.abs(e.touches[0].clientY - startY);
        if (!direction && (dx > 5 || dy > 5)) {
          direction = dx > dy ? 'horizontal' : 'vertical';
        }
        if (direction === 'horizontal') {
          e.preventDefault();
        }
      }, { passive: false });

      this.track.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        isDragging = false;
        if (direction !== 'horizontal') return;
        const endX = e.changedTouches[0].clientX;
        const diff = startX - endX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) this.next();
          else this.prev();
        }
      });

      this.updateSlideWidths();
      window.addEventListener('resize', () => this.updateSlideWidths());
    }

    updateSlideWidths() {
      if (!this.track || !this.slides || this.slides.length === 0) return;

      // Check if this slider should only activate on mobile (used in curated grid)
      const mobileOnly = this.dataset.slidesPerView === '2' && this.classList.contains('fortera-curated__slider');

      const containerWidth = this.track.parentElement.offsetWidth;
      if (containerWidth === 0) return; // Hidden tab, skip

      if (mobileOnly && containerWidth >= 600) {
        // Desktop / wide container: reset to CSS grid layout
        this.slides.forEach((slide) => {
          slide.style.width = '';
          slide.style.marginRight = '';
        });
        this.track.style.transform = '';
        this.maxIndex = 0;
        this._perView = this.slides.length;
        this._stepSize = this.slides.length;
        this._slideWidth = 0;
        this._gap = 0;
        return;
      }

      const gap = 16;
      let perView = this.slidesPerView;
      let peekWidth = 0;

      // Curated slider: 2 per view on mobile
      if (mobileOnly) {
        perView = 2;
        peekWidth = 0;
      } else if (containerWidth < 500) {
        perView = 2;
        peekWidth = 0;
      } else if (containerWidth < 700) {
        perView = Math.min(perView, 2);
      }

      const slideWidth = (containerWidth - peekWidth - gap * perView) / perView;
      this.slides.forEach((slide) => {
        slide.style.width = slideWidth + 'px';
        slide.style.marginRight = gap + 'px';
      });

      // Step size = how many slides to move per click (matches visible count)
      this._stepSize = perView;
      this.maxIndex = Math.max(0, this.slides.length - perView);
      this._perView = perView;
      this._slideWidth = slideWidth;
      this._gap = gap;
      this.goTo(Math.min(this.currentIndex, this.maxIndex));
    }

    goTo(index) {
      this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
      const offset = this.currentIndex * (this._slideWidth + this._gap);
      this.track.style.transform = `translateX(-${offset}px)`;
      this.updateDots();
      this.updateArrows();
    }

    next() {
      const step = this._stepSize || 1;
      if (this.currentIndex < this.maxIndex) {
        this.goTo(Math.min(this.currentIndex + step, this.maxIndex));
      } else {
        this.goTo(0);
      }
    }

    prev() {
      const step = this._stepSize || 1;
      if (this.currentIndex > 0) {
        this.goTo(Math.max(this.currentIndex - step, 0));
      } else {
        this.goTo(this.maxIndex);
      }
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
      });
    }

    updateArrows() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(
        () => this.next(),
        this.autoplayInterval
      );
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('fortera-slider', ForteraSlider);

  /* === Shop The Look - Hotspot Component === */
  class ForteraHotspot extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this._bound = false;
    }

    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      // Use event delegation - children may not be parsed yet when connectedCallback fires
      this.addEventListener('click', (e) => {
        const hotspot = e.target.closest('[data-hotspot]');
        if (!hotspot) return;

        // Lazy-find the slider on first click (guaranteed to be initialized by then)
        if (!this.slider) {
          const section = this.closest('.fortera-shop-look, .fortera-split-look, [id^="ShopLook"], [id^="SplitLook"]');
          if (section) {
            this.slider = section.querySelector('fortera-slider');
          }
        }

        const slideIndex = parseInt(hotspot.dataset.hotspot);
        if (this.slider && typeof this.slider.goTo === 'function') {
          this.slider.goTo(slideIndex);
        }

        // Highlight active hotspot
        this.querySelectorAll('[data-hotspot]').forEach((h) => h.classList.remove('is-active'));
        hotspot.classList.add('is-active');
      });
    }
  }

  customElements.define('fortera-hotspot', ForteraHotspot);

  /* === Bulk Order by Size (B2B) === */
  class ForteraBulkOrder extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex);
      this.colorOptionIndex = parseInt(this.dataset.colorOptionIndex);
      this.variants = JSON.parse(this.querySelector('[data-bulk-variants]').textContent);
      this.rows = this.querySelectorAll('.fortera-bulk-order__row');
      this.totalQtyEl = this.querySelector('[data-bulk-total-qty]');
      this.totalPriceEl = this.querySelector('[data-bulk-total-price]');
      this.warningEl = this.querySelector('[data-bulk-warning]');
      this.submitBtn = this.querySelector('[data-bulk-submit]');
      this.minOrder = parseInt(this.dataset.minOrder) || 20;
      this.surcharge = 0;

      this.colorSwatches = this.querySelectorAll('.fortera-bulk-order__color-swatch');
      this.colorNameEl = this.querySelector('[data-bulk-color-name]');
      this.selectedColor = this.colorSwatches.length > 0 ? this.colorSwatches[0].dataset.color : null;

      this.bindEvents();
      this.bindColorPicker();

      // Set initial color state
      if (this.selectedColor) {
        this.updateVariantsForColor(this.selectedColor);
      }
    }

    bindEvents() {
      this.rows.forEach((row) => {
        const minusBtn = row.querySelector('[data-action="minus"]');
        const plusBtn = row.querySelector('[data-action="plus"]');
        const input = row.querySelector('.fortera-bulk-order__input');

        minusBtn.addEventListener('click', () => {
          const val = parseInt(input.value) || 0;
          input.value = Math.max(0, val - 1);
          this.updateTotals();
        });

        plusBtn.addEventListener('click', () => {
          const val = parseInt(input.value) || 0;
          input.value = val + 1;
          this.updateTotals();
        });

        input.addEventListener('change', () => {
          const val = parseInt(input.value) || 0;
          input.value = Math.max(0, val);
          this.updateTotals();
        });

        input.addEventListener('focus', () => {
          input.select();
        });
      });

    }

    bindColorPicker() {
      this.colorSwatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          // Update active state
          this.colorSwatches.forEach((s) => s.classList.remove('is-active'));
          swatch.classList.add('is-active');

          this.selectedColor = swatch.dataset.color;
          if (this.colorNameEl) {
            this.colorNameEl.textContent = this.selectedColor;
          }

          this.updateVariantsForColor(this.selectedColor);

          // Update main product image to match color
          const colorVariant = this.variants.find((v) =>
            v.options[this.colorOptionIndex] === this.selectedColor && v.featured_image
          );
          if (colorVariant && colorVariant.featured_image) {
            const mainImg = document.querySelector('.product__media-item.is-active img, .product__media-item:first-child img');
            if (mainImg) {
              mainImg.src = colorVariant.featured_image;
            }
          }
        });
      });
    }

    updateVariantsForColor(selectedColor) {
      if (this.colorOptionIndex < 0 || !selectedColor) return;

      this.rows.forEach((row) => {
        const size = row.dataset.size;
        const variant = this.variants.find((v) =>
          v.options[this.sizeOptionIndex] === size &&
          v.options[this.colorOptionIndex] === selectedColor
        );

        if (variant) {
          row.dataset.variantId = variant.id;
          row.dataset.basePrice = variant.price;
          row.dataset.price = variant.price + (this.surcharge || 0);
          row.dataset.available = String(variant.available);
          const effectivePrice = variant.price + (this.surcharge || 0);
          const formatted = (effectivePrice / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
          });
          row.querySelector('.fortera-bulk-order__price').textContent = formatted;
          row.style.display = '';
        } else {
          row.dataset.available = 'false';
          row.style.display = '';
        }
      });

      // Reset quantities on color change
      this.rows.forEach((row) => {
        row.querySelector('.fortera-bulk-order__input').value = 0;
      });
      this.updateTotals();
    }

    updateTotals() {
      let totalQty = 0;
      let totalPrice = 0;

      this.rows.forEach((row) => {
        const qty = parseInt(row.querySelector('.fortera-bulk-order__input').value) || 0;
        const price = parseInt(row.dataset.price) || 0;
        totalQty += qty;
        totalPrice += qty * price;
      });

      this.totalQtyEl.textContent = totalQty;

      // Format price using Shopify money format
      const formatted = (totalPrice / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
      this.totalPriceEl.textContent = formatted;

      // Soft warning: allow add but show note if < min
      if (totalQty > 0 && totalQty < this.minOrder) {
        const remaining = this.minOrder - totalQty;
        this.warningEl.innerHTML = `You need <strong>${remaining} more piece${remaining > 1 ? 's' : ''}</strong> to meet the minimum order of ${this.minOrder}. You can still add to cart and continue shopping.`;
        this.warningEl.style.display = 'block';
        this.submitBtn.disabled = false;
      } else if (totalQty >= this.minOrder) {
        this.warningEl.style.display = 'none';
        this.submitBtn.disabled = false;
      } else {
        this.warningEl.style.display = 'none';
        this.submitBtn.disabled = true;
      }
    }

    setSurcharge(surchargeInCents) {
      this.surcharge = surchargeInCents || 0;
      this.updateRowPrices();
      this.updateTotals();
    }

    updateRowPrices() {
      this.rows.forEach((row) => {
        const basePrice = parseInt(row.dataset.basePrice) || 0;
        const effectivePrice = basePrice + (this.surcharge || 0);
        row.dataset.price = effectivePrice;
        const formatted = (effectivePrice / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        });
        row.querySelector('.fortera-bulk-order__price').textContent = formatted;
      });
    }

    getItems() {
      const items = [];
      this.rows.forEach((row) => {
        const qty = parseInt(row.querySelector('.fortera-bulk-order__input').value) || 0;
        const variantId = parseInt(row.dataset.variantId);
        if (qty > 0 && variantId) {
          items.push({ id: variantId, quantity: qty });
        }
      });
      return items;
    }

    async addAllToCart() {
      const items = [];

      this.rows.forEach((row) => {
        const qty = parseInt(row.querySelector('.fortera-bulk-order__input').value) || 0;
        const variantId = parseInt(row.dataset.variantId);
        if (qty > 0 && variantId) {
          items.push({ id: variantId, quantity: qty });
        }
      });

      if (items.length === 0) return;

      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'ADDING...';

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error('Cart error');

        this.submitBtn.textContent = 'ADDED TO CART!';

        // Reset quantities
        this.rows.forEach((row) => {
          row.querySelector('.fortera-bulk-order__input').value = 0;
        });
        this.updateTotals();

        // Refresh cart icon by re-rendering the header section
        await this.refreshCartIcon();

        setTimeout(() => {
          this.submitBtn.textContent = 'ADD ALL TO CART';
          this.submitBtn.disabled = true;
        }, 2000);
      } catch (err) {
        this.submitBtn.textContent = 'ERROR - TRY AGAIN';
        setTimeout(() => {
          this.submitBtn.textContent = 'ADD ALL TO CART';
          this.submitBtn.disabled = false;
        }, 2000);
      }
    }

    async refreshCartIcon() {
      try {
        // Fetch cart data
        const cartRes = await fetch('/cart.json');
        const cartData = await cartRes.json();
        const count = cartData.item_count;

        // Try to update existing bubble
        let bubble = document.querySelector('.cart-count-bubble');

        if (bubble) {
          // Bubble exists, update count
          const spans = bubble.querySelectorAll('span');
          if (spans[0]) spans[0].textContent = count;
          if (spans[1]) spans[1].textContent = `${count} items`;
        } else {
          // Bubble doesn't exist (cart was empty), create it
          const cartLink = document.querySelector('#cart-icon-bubble, .header__icon--cart, a[href="/cart"]');
          if (cartLink) {
            // Swap empty cart icon to filled
            const emptyIcon = cartLink.querySelector('.icon-cart-empty, [class*="cart-empty"]');
            if (emptyIcon) {
              emptyIcon.outerHTML = emptyIcon.outerHTML.replace('cart-empty', 'cart');
            }

            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            bubble.innerHTML = `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count} items</span>`;
            cartLink.appendChild(bubble);
          }
        }

        // Also dispatch event for cart drawer if enabled
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Try section rendering API for most reliable update
        const sectionRes = await fetch('/?sections=cart-icon-bubble');
        if (sectionRes.ok) {
          const sections = await sectionRes.json();
          const html = sections['cart-icon-bubble'];
          if (html) {
            const cartIconContainer = document.getElementById('cart-icon-bubble');
            if (cartIconContainer) {
              cartIconContainer.innerHTML = html;
            }
          }
        }
      } catch (e) {
        // Silent fail - cart still updated, just icon didn't refresh
      }
    }
  }

  customElements.define('fortera-bulk-order', ForteraBulkOrder);

  /* === Product Card Image Rollover === */
  class ForteraProductCard {
    constructor() {
      this.init();
    }

    init() {
      document
        .querySelectorAll('.fortera-product-card__media')
        .forEach((media) => {
          const primary = media.querySelector(
            '.fortera-product-card__primary-img'
          );
          const hover = media.querySelector(
            '.fortera-product-card__hover-img'
          );
          if (!primary || !hover) return;

          // Preload hover image
          media.addEventListener('mouseenter', () => {
            if (!hover.src && hover.dataset.src) {
              hover.src = hover.dataset.src;
            }
          });
        });
    }
  }

  /* === Product Accordion === */
  class ForteraAccordion extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('.fortera-product__accordion-btn');
      this.init();
    }

    init() {
      this.buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const content = btn.nextElementSibling;
          const isOpen = content.classList.contains('is-open');

          // Close all
          this.querySelectorAll(
            '.fortera-product__accordion-content'
          ).forEach((c) => c.classList.remove('is-open'));
          this.buttons.forEach((b) => b.setAttribute('aria-expanded', 'false'));

          // Toggle current
          if (!isOpen) {
            content.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }
  }

  customElements.define('fortera-accordion', ForteraAccordion);

  /* === Transparent Header on Hero === */
  class ForteraTransparentHeader {
    constructor() {
      this.header = document.querySelector('.header-wrapper');
      if (!this.header) return;
      if (!document.querySelector('.fortera-hero')) return;

      this.isTransparent = true;
      document.body.classList.add('fortera-header-transparent');
      this.init();
    }

    init() {
      const heroHeight =
        document.querySelector('.fortera-hero')?.offsetHeight || 600;

      window.addEventListener('scroll', () => {
        const currentScroll = window.scrollY;

        if (currentScroll > heroHeight * 0.3) {
          this.header.classList.add('scrolled');
        } else {
          this.header.classList.remove('scrolled');
        }
      });
    }
  }

  /* === Quick Add to Cart === */
  class ForteraQuickAdd {
    constructor() {
      this.init();
    }

    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.fortera-product-card__quick-add');
        if (!btn) return;

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        e.preventDefault();
        btn.textContent = 'Adding...';
        btn.disabled = true;

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: parseInt(variantId),
            quantity: 1,
          }),
        })
          .then((res) => res.json())
          .then(() => {
            btn.textContent = 'Added!';
            setTimeout(() => {
              btn.textContent = 'Quick Add';
              btn.disabled = false;
            }, 1500);

            // Update cart count
            const cartCount = document.querySelector('.cart-count-bubble span');
            if (cartCount) {
              const count = parseInt(cartCount.textContent || '0') + 1;
              cartCount.textContent = count;
            }

            // Dispatch cart update event
            document.dispatchEvent(new CustomEvent('cart:updated'));
          })
          .catch(() => {
            btn.textContent = 'Error';
            setTimeout(() => {
              btn.textContent = 'Quick Add';
              btn.disabled = false;
            }, 1500);
          });
      });
    }
  }

  /* === Smooth Scroll for Anchor Links === */
  class ForteraSmoothScroll {
    constructor() {
      document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.addEventListener('click', (e) => {
          const target = document.querySelector(anchor.getAttribute('href'));
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });
    }
  }

  /* === Product Description Show More/Less === */
  class ForteraDescriptionToggle {
    constructor() {
      this.init();
    }
    init() {
      const wrappers = document.querySelectorAll('.fortera-desc-wrapper');
      wrappers.forEach((wrapper) => {
        const content = wrapper.querySelector('.fortera-desc-content');
        const toggle = wrapper.querySelector('.fortera-desc-toggle');
        if (!content || !toggle) return;
        if (toggle.dataset.forteraInit === '1') return;
        toggle.dataset.forteraInit = '1';

        // Hide toggle if content is shorter than collapsed max-height
        const threshold = 180;
        if (content.scrollHeight <= threshold + 20) {
          content.classList.remove('is-collapsed');
          toggle.style.display = 'none';
          return;
        }

        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          if (expanded) {
            content.classList.remove('is-expanded');
            content.classList.add('is-collapsed');
            toggle.setAttribute('aria-expanded', 'false');
            wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else {
            content.classList.remove('is-collapsed');
            content.classList.add('is-expanded');
            toggle.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }
  }

  /* === Product Tabs (Description + FAQ) === */
  class ForteraProductTabs {
    constructor() {
      this.init();
    }
    init() {
      const tabContainers = document.querySelectorAll('.fortera-product-tabs');
      tabContainers.forEach((container) => {
        if (container.dataset.forteraInit === '1') return;
        container.dataset.forteraInit = '1';

        const buttons = container.querySelectorAll('.fortera-product-tabs__btn');
        const panels = container.querySelectorAll('.fortera-product-tabs__panel');

        buttons.forEach((btn) => {
          btn.addEventListener('click', () => {
            const target = btn.dataset.tab;
            buttons.forEach((b) => {
              const active = b.dataset.tab === target;
              b.classList.toggle('is-active', active);
              b.setAttribute('aria-selected', active ? 'true' : 'false');
            });
            panels.forEach((p) => {
              const active = p.dataset.panel === target;
              p.classList.toggle('is-active', active);
              if (active) {
                p.removeAttribute('hidden');
              } else {
                p.setAttribute('hidden', '');
              }
            });
          });
        });
      });
    }
  }

  /* === Back to Top button === */
  class ForteraBackToTop {
    constructor() {
      this.init();
    }
    init() {
      const btn = document.querySelector('.fortera-back-to-top');
      if (!btn || btn.dataset.forteraInit === '1') return;
      btn.dataset.forteraInit = '1';

      const threshold = 500;
      const update = () => {
        const visible = window.scrollY > threshold;
        if (visible) {
          btn.removeAttribute('hidden');
          requestAnimationFrame(() => btn.classList.add('is-visible'));
        } else {
          btn.classList.remove('is-visible');
          setTimeout(() => {
            if (window.scrollY <= threshold) btn.setAttribute('hidden', '');
          }, 200);
        }
      };

      window.addEventListener('scroll', update, { passive: true });
      btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
      update();
    }
  }

  /* === Initialize Everything === */
  function init() {
    new ForteraScrollReveal();
    new ForteraProductCard();
    new ForteraTransparentHeader();
    new ForteraQuickAdd();
    new ForteraSmoothScroll();
    new ForteraDescriptionToggle();
    new ForteraProductTabs();
    new ForteraBackToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section render
  if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
